import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { imageToTokens, search } from '../../../api/client.js';
import LocalCache from '../../../utils/LocalCache.ts';
import { uploadImage } from '../../../api/firebase_storage.ts';
import imageCompression from 'browser-image-compression';
import { MarketUtils } from '@tommyle/monolib/market-utils';

interface SearchResult {
  availablePosts: any[];
  posts: any[];
  colorDistribution: Distribution;
  sizeDistribution: Distribution;
  styleTagDistribution: Distribution;
  departmentDistribution: Distribution;
  categoryDistribution: Distribution;
  categoryFeatureDistribution: Distribution;
  conditionDistribution: Distribution;
  filterOptions: FilterOptions;
  tokenizedQuery?: {
    brand?: string;
    style?: string;
  };
}

interface Distribution {
  sold: Record<string, number>;
  available: Record<string, number>;
}

type Stat = {
  title: string;
  mainValue: number | string;
  secondaryValue?: number | string;
  mainLabel?: string;
  secondaryLabel?: string;
  description?: string;
  insightLabel?: string;
  insightLevel?: 'neutral' | 'success' | 'warning' | 'danger';
};

export interface DistributionRow {
  id: string;
  name: string;
  sold: number;
  available: number;
}

interface DistributionTables {
  colorRows: DistributionRow[];
  sizeRows: DistributionRow[];
  styleRows: DistributionRow[];
  categoryRows: DistributionRow[];
  departmentRows: DistributionRow[];
  featureRows: DistributionRow[];
}

type UsePostGridReturn = {
  postGridData: any[];
  formatTimeSinceSale: (daysSinceSale: number | null) => string;
  formatTimeToSell: (timeToSell: number | null) => string;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  handleSort: (field: string) => void;
  showAvailable: boolean;
  setShowAvailable: (show: boolean) => void;
};

interface FilterOption {
  slug: string;
  display: string;
  count: number;
}

interface FilterOptions {
  brands: FilterOption[];
  categories: FilterOption[];
  sizes: FilterOption[];
  colors: FilterOption[];
  styles: FilterOption[];
  departments: FilterOption[];
  conditions: FilterOption[];
}

interface UseSearchReturn {
  results: SearchResult | null;
  query: string;
  loading: boolean;
  error: string | null;
  errorCode: number | null;
  showSuggestions: boolean;
  stats: Stat[];
  setQuery: (query: string) => void;
  setShowSuggestions: (show: boolean) => void;
  handleSearch: (searchQuery?: string) => void;
  performSearch: (searchQuery: string) => Promise<void>;
  imageToQuery: (imageFile: File) => Promise<string | undefined>;
  initialSearchDone: React.RefObject<boolean>;
  postGrid: UsePostGridReturn;
  distributions: DistributionTables;
  filterOptions: FilterOptions;
  selectedBrands: string[];
  setSelectedBrands: React.Dispatch<React.SetStateAction<string[]>>;
  selectedCategories: string[];
  setSelectedCategories: React.Dispatch<React.SetStateAction<string[]>>;
  selectedSizes: string[];
  setSelectedSizes: React.Dispatch<React.SetStateAction<string[]>>;
  selectedColors: string[];
  setSelectedColors: React.Dispatch<React.SetStateAction<string[]>>;
  selectedStyles: string[];
  setSelectedStyles: React.Dispatch<React.SetStateAction<string[]>>;
  selectedDepartments: string[];
  setSelectedDepartments: React.Dispatch<React.SetStateAction<string[]>>;
  selectedConditions: string[];
  setSelectedConditions: React.Dispatch<React.SetStateAction<string[]>>;
}

const calculateDaysSinceSale = (statusChangedAt: string): number | null => {
  const saleDate = new Date(statusChangedAt);
  const now = new Date();
  return isNaN(saleDate.getTime())
    ? null
    : Math.floor((now.getTime() - saleDate.getTime()) / (1000 * 60 * 60 * 24));
};

const calculateTimeToSell = (statusChangedAt: string, firstPublishedAt: string): number | null => {
  const saleDate = new Date(statusChangedAt);
  const publishDate = new Date(firstPublishedAt);

  if (isNaN(saleDate.getTime()) || isNaN(publishDate.getTime())) {
    return null;
  }

  return Math.floor((saleDate.getTime() - publishDate.getTime()) / (1000 * 60 * 60 * 24));
};

const transformDistributionToRows = (distribution: Distribution | undefined): DistributionRow[] => {
  if (!distribution) return [];

  const allKeys = new Set([
    ...Object.keys(distribution.sold || {}),
    ...Object.keys(distribution.available || {})
  ]);

  return Array.from(allKeys)
    .map(key => ({
      id: key,
      name: key,
      sold: distribution.sold[key] || 0,
      available: distribution.available[key] || 0
    }))
    .sort((a, b) => (b.sold + b.available) - (a.sold + a.available))
    .slice(0, 10);
};

const matchesStyle = (title: string, style: string): boolean => {
  const titleWords = title.toLowerCase().split(/\s+/);
  const styleWords = style.toLowerCase().split(/\s+/);

  console.log(styleWords);

  return styleWords.every(styleWord =>
    titleWords.some(titleWord => titleWord.includes(styleWord))
  );
};

const formatCondition = (condition?: string) => {
  if (!condition) return "Not specified";

  switch (condition) {
    case "nwt":
      return "NWT";
    case "not_nwt":
      return "New";
    case "ret":
      return "Return";
    default:
      return condition;
  }
};

const formatTimeAgo = (
  startDate: string | undefined,
  endDate: Date = new Date(),
  options: {
    includeAgo?: boolean;
    includeUnknown?: boolean;
    includeSameDay?: boolean;
  } = {}
): string => {
  const { includeAgo = false, includeUnknown = true, includeSameDay = false } = options;
  
  if (!startDate) return includeUnknown ? 'Unknown' : '';
  
  const date = new Date(startDate);
  if (isNaN(date.getTime())) return includeUnknown ? 'Unknown' : '';
  
  const daysDiff = Math.floor((endDate.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  
  if (daysDiff === 0) {
    if (includeSameDay) return 'Same day';
    return includeAgo ? 'Today' : '0 days';
  }
  
  if (daysDiff === 1) return includeAgo ? '1 day ago' : '1 day';
  
  if (daysDiff < 30) {
    return includeAgo ? `${daysDiff} days ago` : `${daysDiff} days`;
  }
  
  const months = Math.floor(daysDiff / 30);
  if (months < 12) {
    const monthText = months === 1 ? 'month' : 'months';
    return includeAgo ? `${months} ${monthText} ago` : `${months} ${monthText}`;
  }
  
  const years = Math.floor(months / 12);
  const yearText = years === 1 ? 'year' : 'years';
  return includeAgo ? `${years} ${yearText} ago` : `${years} ${yearText}`;
};

export const useSearch = (): UseSearchReturn => {
  const [results, setResults] = useState<SearchResult | null>(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<number | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const initialSearchDone = useRef(false);
  const searchCache = useRef(new LocalCache());
  const navigate = useNavigate();

  const [sortField, setSortField] = useState('daysSinceSale');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showAvailable, setShowAvailable] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [selectedStyles, setSelectedStyles] = useState<string[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);

  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery || searchQuery.trim() === "") return;

    setLoading(true);
    setError(null);
    setErrorCode(null);
    setShowSuggestions(false);

    try {
      let res = searchCache.current.get(searchQuery);

      if (res === null) {
        res = await search(searchQuery);
        searchCache.current.set(searchQuery, res);
      }

      setResults(res as SearchResult);
      setQuery(searchQuery);
    } catch (err: any) {
      setErrorCode(err?.response?.status || null);

      if (err?.response?.status === 429) {
        setError("Limit reached.");
      } else {
        setError("Something went wrong. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const imageToQuery = async (file: File): Promise<string | undefined> => {
    setLoading(true);
    setError(null);
    setErrorCode(null);
    setShowSuggestions(false);

    try {
      // Basic validation
      if (!file) {
        throw new Error('No image file received');
      }

      // For iPhone camera photos, create a new blob/file
      let processableFile;
      try {
        const blob = new Blob([file], { type: 'image/jpeg' });
        processableFile = new File([blob], 'camera_photo.jpg', { type: 'image/jpeg' });
      } catch (e) {
        console.error('Error creating processable file:', e);
        processableFile = file;
      }

      // Compress image
      const options = {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 1024,
        useWebWorker: false,
        fileType: 'image/jpeg',
        initialQuality: 0.7,
      };
      
      const compressedFile = await imageCompression(processableFile, options);
      const result = await uploadImage(compressedFile, 'images/search');

      const tokenResult = await imageToTokens(result.url);
      
      const tokenizedQuery = tokenResult.tokens;
      const query = tokenizedQuery?.brand + " " + tokenizedQuery?.style;

      return query;
    } catch (err) {
      console.error('Error handling image upload:', err);
      setError("Something went wrong. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const updateURL = useCallback((searchQuery: string | null = null) => {
    const params = new URLSearchParams();

    if (searchQuery || query) {
      params.set('q', searchQuery || query);
    }

    if (selectedBrands.length) params.set('brands', selectedBrands.join(','));
    if (selectedStyles.length) params.set('styles', selectedStyles.join(','));
    if (selectedCategories.length) params.set('categories', selectedCategories.join(','));
    if (selectedSizes.length) params.set('sizes', selectedSizes.join(','));
    if (selectedColors.length) params.set('colors', selectedColors.join(','));
    if (selectedDepartments.length) params.set('departments', selectedDepartments.join(','));
    if (selectedConditions.length) params.set('conditions', selectedConditions.join(','));

    navigate(`?${params.toString()}`, { replace: true });
  }, [navigate, query, selectedBrands, selectedStyles, selectedCategories, selectedSizes, selectedColors, selectedDepartments, selectedConditions]);

  useEffect(() => {
    if (!results || !results.filterOptions) return;

    const params = new URLSearchParams(window.location.search);

    const updateFilterFromParam = (param: string, setter: (values: string[]) => void, options: FilterOption[]) => {
      const values = params.get(param)?.split(',') ?? [];
      const validValues = values.filter(value =>
        options.some(option => option.slug === value)
      );
      if (validValues.length) setter(validValues);
    };

    updateFilterFromParam('brands', setSelectedBrands, results.filterOptions.brands);
    updateFilterFromParam('styles', setSelectedStyles, results.filterOptions.styles);
    updateFilterFromParam('categories', setSelectedCategories, results.filterOptions.categories);
    updateFilterFromParam('sizes', setSelectedSizes, results.filterOptions.sizes);
    updateFilterFromParam('colors', setSelectedColors, results.filterOptions.colors);
    updateFilterFromParam('departments', setSelectedDepartments, results.filterOptions.departments);
    updateFilterFromParam('conditions', setSelectedConditions, results.filterOptions.conditions);

    // If no brands selected from URL, select the top brand
    if (!params.has('brands') && results.filterOptions.brands.length > 0) {
      setSelectedBrands([results.filterOptions.brands[0].slug]);
    }

    // If no styles selected from URL, select the top style
    if (!params.has('styles') && results.filterOptions.styles.length > 0) {
      setSelectedStyles([results.filterOptions.styles[0].slug]);
    }
  }, [results]);

  useEffect(() => {
    if (!results) return;
    updateURL();
  }, [selectedBrands, selectedStyles, selectedCategories, selectedSizes, selectedColors, selectedDepartments, selectedConditions, updateURL, results]);

  const handleSearch = useCallback((searchQuery: string = query) => {
    performSearch(searchQuery);
    updateURL(searchQuery);
  }, [query, performSearch, updateURL]);

  const getFilteredPosts = useCallback((posts: any[]) => {
    if (!posts) return [];

    return posts.filter(post => {
      const brandMatch = selectedBrands.length === 0 ||
        (post.brand_obj?.slug && selectedBrands.includes(post.brand_obj.slug));

      const categoryMatch = selectedCategories.length === 0 ||
        (post.category_features?.[0]?.slug && selectedCategories.includes(post.category_features[0].slug));

      const sizeMatch = selectedSizes.length === 0 ||
        (post.size && selectedSizes.includes(post.size.toLowerCase().replace(/\s+/g, '-')));

      const colorMatch = selectedColors.length === 0 ||
        (post.colors && post.colors.some(color =>
          selectedColors.includes(color.name.toLowerCase().replace(/\s+/g, '-'))
        ));

      const styleMatch = selectedStyles.length === 0 ||
        (post.title && selectedStyles.some(style =>
          matchesStyle(post.title, style)
        ));

      const departmentMatch = selectedDepartments.length === 0 ||
        (post.department?.slug && selectedDepartments.includes(post.department.slug));

      const conditionMatch = selectedConditions.length === 0 ||
        selectedConditions.includes(formatCondition(post.condition).toLowerCase().replace(/\s+/g, '-'));

      return brandMatch && categoryMatch && sizeMatch && colorMatch && styleMatch && departmentMatch && conditionMatch;
    });
  }, [selectedBrands, selectedCategories, selectedSizes, selectedColors, selectedStyles, selectedDepartments, selectedConditions]);

  const soldPosts = useMemo(() => {
    if (!results?.posts) return [];
    return getFilteredPosts(results.posts);
  }, [getFilteredPosts, results]);

  const availablePosts = useMemo(() => {
    if (!results?.availablePosts) return [];
    return getFilteredPosts(results.availablePosts);
  }, [getFilteredPosts, results]);

  const formatTimeSinceListing = useCallback((firstPublishedAt: string | undefined): string => {
    return formatTimeAgo(firstPublishedAt);
  }, []);

  const postGridData = useMemo(() => {
    if (!results || (!soldPosts && !availablePosts)) return [];

    const postsToUse = showAvailable ? availablePosts : soldPosts;
    if (!postsToUse) return [];

    return postsToUse.map(post => ({
      id: post.id,
      title: post.title,
      brand: post.brand,
      image: post.picture_url,
      price: post.price,
      description: post.description,
      condition: formatCondition(post.condition),
      daysSinceSale: showAvailable ? null : calculateDaysSinceSale(post.inventory?.status_changed_at),
      timeToSell: showAvailable ? null : calculateTimeToSell(
        post.inventory?.status_changed_at,
        post.first_published_at
      ),
      daysSinceListing: formatTimeSinceListing(post.first_published_at),
      shares: post.aggregates?.shares || 0,
      comments: post.aggregates?.comments || 0,
      likes: post.aggregates?.likes || 0,
      active_buyer_offers: post.active_buyer_offers || 0,
      has_offer: post.has_offer,
    }))
      .sort((a, b) => {
        const sortValue: Record<string, () => number> = {
          price: () => sortDirection === 'asc' ? a.price - b.price : b.price - a.price,
          daysSinceSale: () => {
            if (a.daysSinceSale === null) return sortDirection === 'asc' ? 1 : -1;
            if (b.daysSinceSale === null) return sortDirection === 'asc' ? -1 : 1;
            return sortDirection === 'asc' ? a.daysSinceSale - b.daysSinceSale : b.daysSinceSale - a.daysSinceSale;
          },
          timeToSell: () => {
            if (a.timeToSell === null) return sortDirection === 'asc' ? 1 : -1;
            if (b.timeToSell === null) return sortDirection === 'asc' ? -1 : 1;
            return sortDirection === 'asc' ? a.timeToSell - b.timeToSell : b.timeToSell - a.timeToSell;
          },
          shares: () => sortDirection === 'asc' ? a.shares - b.shares : b.shares - a.shares,
          comments: () => sortDirection === 'asc' ? a.comments - b.comments : b.comments - a.comments,
          likes: () => sortDirection === 'asc' ? a.likes - b.likes : b.likes - a.likes,
        };

        return sortValue[sortField]?.() ?? 0;
      });
  }, [results, showAvailable, availablePosts, soldPosts, sortField, sortDirection, formatTimeSinceListing]);

  const formatTimeSinceSale = useCallback((daysSinceSale: number | null): string => {
    if (daysSinceSale === null) return 'Recently';
    return formatTimeAgo(new Date(Date.now() - daysSinceSale * 24 * 60 * 60 * 1000).toISOString(), new Date(), { includeAgo: true });
  }, []);

  const formatTimeToSell = useCallback((timeToSell: number | null): string => {
    if (timeToSell === null) return 'Unknown';
    return formatTimeAgo(new Date(Date.now() - timeToSell * 24 * 60 * 60 * 1000).toISOString(), new Date(), { includeSameDay: true });
  }, []);

  const handleSort = useCallback((field: string) => {
    setSortDirection(prevDirection => (sortField === field && prevDirection === 'desc' ? 'asc' : 'desc'));
    setSortField(field);
  }, [sortField]);

  const distributions = useMemo<DistributionTables>(() => {
    if (!results) {
      return {
        colorRows: [],
        sizeRows: [],
        styleRows: [],
        categoryRows: [],
        departmentRows: [],
        featureRows: [],
      };
    }

    return {
      colorRows: transformDistributionToRows(results.colorDistribution),
      sizeRows: transformDistributionToRows(results.sizeDistribution),
      styleRows: transformDistributionToRows(results.styleTagDistribution),
      categoryRows: transformDistributionToRows(results.categoryDistribution),
      departmentRows: transformDistributionToRows(results.departmentDistribution),
      featureRows: transformDistributionToRows(results.categoryFeatureDistribution),
    };
  }, [results]);

  const stats = useMemo<Stat[]>(() => {
    if (!results || !soldPosts || !availablePosts) return [];
    
    // Filters have already been applied to soldPosts and availablePosts
    return MarketUtils.getTrendAnalysis(query, soldPosts, availablePosts, {}).stats;
  }, [results, soldPosts, availablePosts, query]);

  return {
    results,
    stats,
    query,
    loading,
    error,
    errorCode,
    showSuggestions,
    setQuery,
    setShowSuggestions,
    handleSearch,
    performSearch,
    imageToQuery,
    initialSearchDone,
    postGrid: {
      postGridData,
      formatTimeSinceSale,
      formatTimeToSell,
      sortField,
      sortDirection,
      handleSort,
      showAvailable,
      setShowAvailable
    },
    distributions,
    filterOptions: results?.filterOptions ?? {
      brands: [],
      categories: [],
      sizes: [],
      colors: [],
      styles: [],
      departments: [],
      conditions: [],
    },
    selectedBrands,
    setSelectedBrands,
    selectedCategories,
    setSelectedCategories,
    selectedSizes,
    setSelectedSizes,
    selectedColors,
    setSelectedColors,
    selectedStyles,
    setSelectedStyles,
    selectedDepartments,
    setSelectedDepartments,
    selectedConditions,
    setSelectedConditions,
  };
}; 